import {
  INfaLfaService,
  ApproveStageDTO,
  RejectStageDTO,
  SendBackStageDTO,
  RequestClarificationDTO,
  RespondClarificationDTO,
  DelegateApprovalDTO,
  EvaluateWorkflowParams,
} from '../api/NfaLfaService';
import {
  NoteForApproval,
  LetterForAward,
  ClarificationRecord,
  DelegationRecord,
  ApprovalRuleMatch,
  LfaStatus,
  NfaStatus,
} from '@/types/domain/nfa-lfa';
import { MOCK_NFA, MOCK_LFA } from '@/mocks/database';
import { processQuery, getEntityById } from './pagination';
import { env } from '@/config/env';
import { notifyGlobal } from '@/context/NotificationContext';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class MockNfaLfaService implements INfaLfaService {
  private nfas: NoteForApproval[] = [...MOCK_NFA];
  private lfas: LetterForAward[] = [...MOCK_LFA];
  private clarifications: ClarificationRecord[] = [];
  private delegations: DelegationRecord[] = [];

  async getNfas(params?: {
    status?: string;
    search?: string;
    departmentCode?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ nfas: NoteForApproval[]; total: number }> {
    await delay(env.mockDelayMs);
    const queryResult = processQuery(this.nfas, {
      page: params?.page || 1,
      pageSize: params?.pageSize || 25,
      search: params?.search,
      filters: {
        status: params?.status,
        departmentCode: params?.departmentCode,
      },
    });

    return { nfas: queryResult.data, total: queryResult.total };
  }

  async getNfaById(id: string): Promise<NoteForApproval | null> {
    await delay(env.mockDelayMs);
    const cleanId = String(id).trim().toLowerCase();
    const cleanDigits = cleanId.replace(/[^0-9]/g, '');

    const found = this.nfas.find(
      (n) =>
        n.id.toLowerCase() === cleanId ||
        n.nfaNumber.toLowerCase() === cleanId ||
        n.requestNumber?.toLowerCase() === cleanId ||
        n.purchaseRequestId?.toLowerCase() === cleanId ||
        (cleanDigits &&
          (n.id.replace(/[^0-9]/g, '') === cleanDigits ||
            n.nfaNumber.replace(/[^0-9]/g, '') === cleanDigits ||
            (n.requestNumber && n.requestNumber.replace(/[^0-9]/g, '') === cleanDigits) ||
            (n.purchaseRequestId && n.purchaseRequestId.replace(/[^0-9]/g, '') === cleanDigits)))
    );
    if (found) return found;
    if (this.nfas.length > 0) {
      const fallback = Object.assign({}, this.nfas[0], { id, nfaNumber: id }) as NoteForApproval;
      this.nfas.unshift(fallback);
      return fallback;
    }
    return null;
  }

  async getLfas(): Promise<LetterForAward[]> {
    await delay(env.mockDelayMs);
    return [...this.lfas];
  }

  async getLfaById(id: string): Promise<LetterForAward | null> {
    await delay(env.mockDelayMs);
    const found = getEntityById(this.lfas, id, 'id') || getEntityById(this.lfas, id, 'lfaNumber');
    if (found) return found;
    if (this.lfas.length > 0) {
      const fallback = Object.assign({}, this.lfas[0], { id, lfaNumber: id }) as LetterForAward;
      this.lfas.unshift(fallback);
      return fallback;
    }
    return null;
  }

  async evaluateWorkflowRules(params: EvaluateWorkflowParams): Promise<any> {
    return this.evaluateWorkflowRule(params);
  }

  async evaluateWorkflowRule(params: EvaluateWorkflowParams): Promise<{
    matchedRule: ApprovalRuleMatch;
    approvalMode: 'SEQUENTIAL' | 'PARALLEL';
    stages: Array<{
      stageNumber: number;
      stageName: string;
      approverRole: string;
      approverName: string;
      approvalMode: 'SEQUENTIAL' | 'PARALLEL';
      isMandatory: boolean;
    }>;
  }> {
    await delay(env.mockDelayMs);
    const isHighValue = params.amount > 50000;

    return {
      matchedRule: {
        ruleId: isHighValue ? 'HIGH-VALUE-PARALLEL-01' : 'RULE-STD-01',
        ruleName: isHighValue ? 'High Value Dual Parallel Matrix' : 'Standard Threshold Approval',
        ruleVersion: '1.0',
        effectiveFrom: '2026-01-01',
        matchedConditions: [{ condition: 'Amount Threshold', passed: true, details: `Amount $${params.amount} evaluated.` }],
      },
      approvalMode: isHighValue ? 'PARALLEL' : 'SEQUENTIAL',
      stages: [
        {
          stageNumber: 1,
          stageName: 'Technical & Commercial Clearance',
          approverRole: 'Department Head',
          approverName: 'Marcus Vance',
          approvalMode: 'SEQUENTIAL',
          isMandatory: true,
        },
        {
          stageNumber: 2,
          stageName: 'Technical Evaluation',
          approverRole: 'Technical Lead',
          approverName: 'Alex Rivera',
          approvalMode: 'PARALLEL',
          isMandatory: true,
        },
        {
          stageNumber: 3,
          stageName: 'Commercial Signoff',
          approverRole: 'Procurement Head',
          approverName: 'Elena Gonzalez',
          approvalMode: 'PARALLEL',
          isMandatory: true,
        },
        {
          stageNumber: 4,
          stageName: 'Executive Financial Signoff',
          approverRole: 'Finance Director',
          approverName: 'Rachel Chen',
          approvalMode: 'SEQUENTIAL',
          isMandatory: true,
        },
      ],
    };
  }

  async approveStage(dto: ApproveStageDTO): Promise<NoteForApproval> {
    await delay(env.mockDelayMs);
    const nfa = await this.getNfaById(dto.nfaId);
    if (!nfa) throw new Error('NFA record not found');

    if (dto.version !== undefined && nfa.version !== undefined && dto.version !== nfa.version) {
      throw new Error('CONCURRENCY_CONFLICT: NFA was modified by another approver.');
    }

    const stage = nfa.approvalChain.find((s) => s.id === dto.stageId || String(s.stageNumber) === String(dto.stageId));
    if (stage) {
      stage.status = 'Approved';
      stage.actionDate = new Date().toISOString();
      stage.comments = dto.comments || 'Approved';
    }

    nfa.version = (nfa.version || 1) + 1;
    nfa.updatedAt = new Date().toISOString();

    const allDone = nfa.approvalChain.every((s) => s.status === 'Approved');
    if (allDone) {
      nfa.status = 'Approved';
      notifyGlobal('NFA Fully Approved', `Note for Approval ${nfa.nfaNumber} has been fully approved!`, 'StatusChange', nfa.id);
    } else {
      const nextPending = nfa.approvalChain.find((s) => s.status === 'Pending');
      if (nextPending) {
        nextPending.status = 'InProgress';
      }
    }

    return nfa;
  }

  async rejectStage(dto: RejectStageDTO): Promise<NoteForApproval> {
    await delay(env.mockDelayMs);
    const nfa = await this.getNfaById(dto.nfaId);
    if (!nfa) throw new Error('NFA record not found');

    const stage = nfa.approvalChain.find((s) => s.id === dto.stageId || String(s.stageNumber) === String(dto.stageId));
    if (stage) {
      stage.status = 'Rejected';
      stage.actionDate = new Date().toISOString();
      stage.comments = dto.reason || dto.comments || 'Rejected';
    }

    nfa.status = 'Rejected';
    nfa.updatedAt = new Date().toISOString();
    return nfa;
  }

  async sendBackStage(dto: SendBackStageDTO): Promise<NoteForApproval> {
    await delay(env.mockDelayMs);
    const nfa = await this.getNfaById(dto.nfaId);
    if (!nfa) throw new Error('NFA record not found');

    nfa.status = 'Draft';
    nfa.updatedAt = new Date().toISOString();
    return nfa;
  }

  async requestClarification(dto: RequestClarificationDTO): Promise<ClarificationRecord> {
    await delay(env.mockDelayMs);
    const nfa = await this.getNfaById(dto.nfaId);
    const record: ClarificationRecord = {
      id: `CLAR-${Date.now()}`,
      requestedByUserId: 'usr-2',
      requestedByName: 'Rachel Chen',
      requestedByRole: 'Finance Director',
      question: dto.question,
      requestedAt: new Date().toISOString(),
      dueDate: dto.dueDate || '2026-10-30',
      status: 'Pending',
    };
    if (nfa) {
      nfa.status = 'ClarificationRequested';
      if (!nfa.clarifications) nfa.clarifications = [];
      nfa.clarifications.unshift(record);
      nfa.updatedAt = new Date().toISOString();
    }
    this.clarifications.unshift(record);
    return record;
  }

  async respondClarification(dto: RespondClarificationDTO): Promise<NoteForApproval> {
    await delay(env.mockDelayMs);
    const nfa = await this.getNfaById(dto.nfaId);
    if (!nfa) throw new Error('NFA not found');

    if (!nfa.clarifications) nfa.clarifications = [];
    let clar = this.clarifications.find((c) => c.id === dto.clarificationId) || nfa.clarifications.find((c) => c.id === dto.clarificationId);
    if (!clar) {
      clar = {
        id: dto.clarificationId,
        requestedByUserId: 'usr-2',
        requestedByName: 'Rachel Chen',
        requestedByRole: 'Finance Director',
        question: 'Clarification query',
        response: dto.response,
        status: 'Responded',
        requestedAt: new Date().toISOString(),
        respondedAt: new Date().toISOString(),
      } as any;
      nfa.clarifications.unshift(clar!);
    } else {
      clar.status = 'Responded';
      clar.response = dto.response;
      clar.respondedAt = new Date().toISOString();
    }

    nfa.status = 'UnderApproval';
    nfa.updatedAt = new Date().toISOString();
    return nfa;
  }

  async delegateApproval(dto: DelegateApprovalDTO): Promise<DelegationRecord> {
    await delay(env.mockDelayMs);
    const delegation: DelegationRecord = {
      id: `DEL-${Date.now()}`,
      fromUserId: 'usr-1',
      fromUserName: 'Marcus Vance',
      fromUserRole: 'Department Head',
      toUserId: dto.toUserId,
      toUserName: dto.toUserName,
      toUserRole: dto.toUserRole,
      startDate: dto.startDate,
      endDate: dto.endDate,
      reason: dto.reason,
      isActive: true,
      createdAt: new Date().toISOString(),
    };
    this.delegations.unshift(delegation);
    const nfa = await this.getNfaById((dto as any).nfaId || 'nfa-1001');
    if (nfa) {
      if (!nfa.delegations) nfa.delegations = [];
      nfa.delegations.unshift(delegation);
    }
    notifyGlobal('Approval Delegated', `Approval delegated to ${dto.toUserName} until ${dto.endDate}.`, 'System');
    return delegation;
  }

  async createNfa(data: Partial<NoteForApproval>): Promise<NoteForApproval> {
    await delay(env.mockDelayMs);
    const seq = Math.floor(1000 + Math.random() * 9000);
    const nfaNumber = `NFA-${seq}`;

    const newNfa: NoteForApproval = {
      id: nfaNumber,
      nfaNumber,
      purchaseRequestId: data.purchaseRequestId || `pr-${seq}`,
      requestNumber: data.requestNumber || `REQ-${seq}`,
      title: data.title || 'Note for Approval',
      subject: data.subject || data.title || 'Subject Approval',
      backgroundContext: data.backgroundContext || 'NFA Background context',
      commercialJustification: data.commercialJustification || 'NFA Commercial justification',
      estimatedCost: data.estimatedCost || 75000,
      currency: data.currency || 'USD',
      savingsEstimated: 5000,
      budgetLineItem: 'BL-IT-101',
      costCenterCode: data.costCenterCode || 'CC-IT-101',
      departmentCode: data.departmentCode || 'DEP-IT',
      departmentName: data.departmentName || 'Information Technology',
      plantCode: data.plantCode || 'PLANT-1000',
      plantName: data.plantName || 'Austin Operations Plant',
      capexOpex: data.capexOpex || 'OPEX',
      procurementCategory: data.procurementCategory || 'IT Hardware',
      status: 'UnderApproval' as NfaStatus,
      version: 1,
      workflowType: 'NFA',
      approvalMode: 'SEQUENTIAL',
      approvalChain: data.approvalChain || [
        {
          id: `STG-${seq}-1`,
          stageNumber: 1,
          stageName: 'Department Signoff',
          approverRole: 'Department Head',
          approverUserId: 'usr-1',
          approverName: 'Marcus Vance',
          approvalMode: 'SEQUENTIAL',
          status: 'InProgress',
          isMandatory: true,
        },
        {
          id: `STG-${seq}-2`,
          stageNumber: 2,
          stageName: 'Finance Signoff',
          approverRole: 'Finance Director',
          approverUserId: 'usr-2',
          approverName: 'Rachel Chen',
          approvalMode: 'SEQUENTIAL',
          status: 'Pending',
          isMandatory: true,
        },
      ],
      matchedRule: {
        ruleId: 'RULE-STD-01',
        ruleName: 'Standard Threshold Approval',
        ruleVersion: '1.0',
        effectiveFrom: '2026-01-01',
        matchedConditions: [{ condition: 'Threshold Check', passed: true }],
      },
      delegations: [],
      clarifications: [],
      sla: {
        slaHours: 48,
        targetTimestamp: '2026-10-30T17:00:00Z',
        remainingMinutes: 2880,
        status: 'OnTrack',
        reminderSent: false,
      },
      preparedByUserId: 'usr-1',
      preparedByName: 'Sarah Jenkins',
      preparedByRole: 'Requisitioner',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.nfas.unshift(newNfa);
    return newNfa;
  }

  async updateNfa(id: string, data: Partial<NoteForApproval>): Promise<NoteForApproval> {
    await delay(env.mockDelayMs);
    const index = this.nfas.findIndex((n) => n.id === id || n.nfaNumber === id);
    if (index === -1) {
      throw new Error(`Note for Approval ${id} not found.`);
    }
    const current = this.nfas[index]!;
    const updated: NoteForApproval = {
      ...current,
      ...data,
      version: (current.version || 1) + 1,
    };
    this.nfas[index] = updated;
    notifyGlobal('NFA Updated', `Note for Approval ${updated.nfaNumber} successfully updated.`, 'System');
    return updated;
  }

  async deleteNfa(id: string): Promise<boolean> {
    await delay(env.mockDelayMs);
    const initialLen = this.nfas.length;
    const target = this.nfas.find((n) => n.id === id || n.nfaNumber === id);
    this.nfas = this.nfas.filter((n) => n.id !== id && n.nfaNumber !== id);
    if (this.nfas.length < initialLen) {
      notifyGlobal('NFA Deleted', `Note for Approval ${target?.nfaNumber || id} removed from ledger.`, 'System');
      return true;
    }
    return false;
  }

  async createLfa(data: Partial<LetterForAward>): Promise<LetterForAward> {
    await delay(env.mockDelayMs);
    const seq = Math.floor(1000 + Math.random() * 9000);
    const lfaNumber = `LFA-${seq}`;

    const newLfa: LetterForAward = {
      id: lfaNumber,
      lfaNumber,
      nfaId: data.nfaId || `nfa-${seq}`,
      nfaNumber: data.nfaNumber || `NFA-${seq}`,
      purchaseRequestId: data.purchaseRequestId || `pr-${seq}`,
      requestNumber: data.requestNumber || `REQ-${seq}`,
      title: data.title || 'Letter for Award',
      rfqId: data.rfqId || `rfq-${seq}`,
      rfqNumber: data.rfqNumber || `RFQ-${seq}`,
      quotationId: data.quotationId || `quo-${seq}`,
      vendorId: data.vendorId || 'vend-1',
      vendorName: data.vendorName || 'Apex Hardware Solutions',
      vendorCode: data.vendorCode || 'VND-101',
      awardedValue: data.awardedValue || 75000,
      currency: data.currency || 'USD',
      effectiveDate: new Date().toISOString(),
      expiryDate: '2027-12-31',
      paymentTerms: data.paymentTerms || 'Net 30',
      deliveryTerms: data.deliveryTerms || 'DDP',
      status: 'Awarded' as LfaStatus,
      version: 1,
      issuedByUserId: 'usr-1',
      issuedByName: 'David Sterling',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.lfas.unshift(newLfa);
    return newLfa;
  }

  async updateLfa(id: string, data: Partial<LetterForAward>): Promise<LetterForAward> {
    await delay(env.mockDelayMs);
    const index = this.lfas.findIndex((l) => l.id === id || l.lfaNumber === id);
    if (index === -1) {
      throw new Error(`Letter for Award ${id} not found.`);
    }
    const current = this.lfas[index]!;
    const updated: LetterForAward = {
      ...current,
      ...data,
      version: (current.version || 1) + 1,
      updatedAt: new Date().toISOString(),
    };
    this.lfas[index] = updated;
    notifyGlobal('LFA Updated', `Letter for Award ${updated.lfaNumber} updated successfully.`, 'System');
    return updated;
  }

  async deleteLfa(id: string): Promise<boolean> {
    await delay(env.mockDelayMs);
    const initialLen = this.lfas.length;
    const target = this.lfas.find((l) => l.id === id || l.lfaNumber === id);
    this.lfas = this.lfas.filter((l) => l.id !== id && l.lfaNumber !== id);
    if (this.lfas.length < initialLen) {
      notifyGlobal('LFA Deleted', `Letter for Award ${target?.lfaNumber || id} removed from ledger.`, 'System');
      return true;
    }
    return false;
  }

  syncNfaRecord(nfa: NoteForApproval): NoteForApproval {
    const idx = this.nfas.findIndex((n) => n.id === nfa.id || n.nfaNumber === nfa.nfaNumber);
    if (idx >= 0) {
      this.nfas[idx] = Object.assign({}, this.nfas[idx], nfa);
      return this.nfas[idx]!;
    }
    this.nfas.unshift(nfa);
    return nfa;
  }

  syncLfaRecord(lfa: LetterForAward): LetterForAward {
    const idx = this.lfas.findIndex((l) => l.id === lfa.id || l.lfaNumber === lfa.lfaNumber);
    if (idx >= 0) {
      this.lfas[idx] = Object.assign({}, this.lfas[idx], lfa);
      return this.lfas[idx]!;
    }
    this.lfas.unshift(lfa);
    return lfa;
  }
}

export const mockNfaLfaService = new MockNfaLfaService();
